import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});

  // Hero slides data with reliable image URLs
  const slides = [
    {
      title: "Ride the Future",
      subtitle: "with Janvahan",
      description: "Experience the thrill of electric mobility with India's most advanced electric bikes",
      buttonText: "Explore Models",
      buttonLink: "/products",
      secondaryButtonText: "Book Test Ride",
      secondaryButtonLink: "/test-ride",
      image: "/images/banner-3.png",
      fallbackImage: "/images/banner-3.png"
    },
    {
      title: "Zero Emissions",
      subtitle: "Pure Performance",
      description: "Join the green revolution with eco-friendly electric bikes that don't compromise on power",
      buttonText: "Learn More",
      buttonLink: "/about",
      secondaryButtonText: "View Models",
      secondaryButtonLink: "/products",
      image: "/images/banner-2.png",
      fallbackImage: "/images/banner-2.png"
    },
    {
      title: "Smart Technology",
      subtitle: "Connected Riding",
      description: "AI-powered battery management, GPS tracking, and mobile app integration for the ultimate riding experience",
      buttonText: "Discover Features",
      buttonLink: "/technology",
      secondaryButtonText: "Book Now",
      secondaryButtonLink: "/book",
      image: "/images/banner-3.png",
      fallbackImage: "/images/banner-3.png"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Preload images
    slides.forEach((slide, index) => {
      const img = new Image();
      img.src = slide.image;
      img.onload = () => {
        setLoadedImages(prev => ({ ...prev, [index]: true }));
      };
      img.onerror = () => {
        // If main image fails, try fallback
        const fallbackImg = new Image();
        fallbackImg.src = slide.fallbackImage;
        fallbackImg.onload = () => {
          setLoadedImages(prev => ({ ...prev, [index]: true }));
        };
      };
    });
    
    // Auto-rotate slides every 5 seconds
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Get current slide image
  const getCurrentImage = () => {
    const currentImage = slides[currentSlide].image;
    return currentImage;
  };

  return (
    <div className="relative bg-black text-white overflow-hidden min-h-screen">
      {/* Background Image with proper styling */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
        style={{
          backgroundImage: `url(${getCurrentImage()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay - makes text readable */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          {/* Animated Badge */}
          <div className="animate-bounce mb-6">
            <span className="inline-block bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
              🚀 Electric Revolution
            </span>
          </div>
          
          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            {slides[currentSlide].title}
            <br />
            <span className="text-red-600 relative inline-block">
              {slides[currentSlide].subtitle}
              <svg className="absolute bottom-0 left-0 w-full h-3 text-red-600" viewBox="0 0 100 10">
                <path d="M0,5 Q25,0 50,5 T100,5" stroke="currentColor" fill="none" strokeWidth="2"/>
              </svg>
            </span>
          </h1>
          
          {/* Description */}
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-gray-200">
            {slides[currentSlide].description}
          </p>
          
          {/* CTA Buttons */}
          <div className="space-x-4">
            <Link 
              to={slides[currentSlide].buttonLink} 
              className="inline-block bg-red-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-red-700 transition transform hover:scale-105 shadow-lg"
            >
              {slides[currentSlide].buttonText} →
            </Link>
            <Link 
              to={slides[currentSlide].secondaryButtonLink} 
              className="inline-block border-2 border-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-white hover:text-black transition transform hover:scale-105"
            >
              {slides[currentSlide].secondaryButtonText}
            </Link>
          </div>
          
          {/* Stats Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-20 transition">
              <div className="text-3xl font-bold text-red-600">80+ km</div>
              <div className="text-gray-200">Range per charge</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-20 transition">
              <div className="text-3xl font-bold text-red-600">75 km/h</div>
              <div className="text-gray-200">Top Speed</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 hover:bg-opacity-20 transition">
              <div className="text-3xl font-bold text-red-600">2 hrs</div>
              <div className="text-gray-200">Fast Charging</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Slide Navigation Dots */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSlide === index 
                ? 'bg-red-600 w-8' 
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 transition z-20"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 transition z-20"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-2 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;